import React from 'react';
import { useIntl } from 'react-intl';

import { TruncatedLink } from '@components/common/react-table/TruncatedLink';
import { TruncatedText } from '@components/common/react-table/TruncatedText';
import { TwoLineCell } from '@components/common/react-table/TwoLineCell';
import { suggestedDownloadFileName } from '@utils/miscellaneous/file.utils';

import type { IHistoricalNotice } from '../constants';

function formatActionRequired(
    value: boolean | null | undefined,
    formatMessage: (descriptor: { id: string }) => string,
): string {
    if (value === true) {
        return formatMessage({ id: 'common.yes' });
    }
    if (value === false) {
        return formatMessage({ id: 'common.no' });
    }
    return '';
}

const ActionRequiredCell = ({ value }: { value: boolean | null }) => {
    const intl = useIntl();
    return <TruncatedText text={formatActionRequired(value, intl.formatMessage)} />;
};

const MemberNameCell = ({ row }: { row: { original: IHistoricalNotice } }) => {
    const intl = useIntl();
    const notice = row.original;
    const memberDob = notice.memberDob?.trim();

    return (
        <TwoLineCell
            primary={notice.memberName ?? ''}
            secondary={
                memberDob
                    ? intl.formatMessage({ id: 'historicalNotices.table.memberDob' }, { dob: memberDob })
                    : ''
            }
        />
    );
};

export const historicalNoticesTableColumns = [
    {
        id: 'subject',
        Header: 'common.subject',
        accessor: 'subject',
        Cell: ({ row }: { row: { original: IHistoricalNotice } }) => {
            const notice = row.original;
            const subject = notice.subject ?? '';

            if (!notice.noticeUrl) {
                return <TruncatedText text={subject} />;
            }

            return (
                <TruncatedLink
                    href={notice.noticeUrl}
                    text={subject}
                    download={suggestedDownloadFileName(subject)}
                />
            );
        },
    },
    {
        id: 'type',
        Header: 'common.type',
        accessor: 'type',
        isSortEnabled: false,
        Cell: ({ value }: { value: string }) => <TruncatedText text={value ?? ''} />,
    },
    {
        id: 'actionRequired',
        Header: 'historicalNotices.table.actionRequired',
        accessor: 'actionRequired',
        isSortEnabled: false,
        Cell: ({ value }: { value: boolean | null }) => <ActionRequiredCell value={value} />,
    },
    {
        id: 'memberName',
        Header: 'common.member_name',
        accessor: 'memberName',
        isSortEnabled: false,
        Cell: MemberNameCell,
    },
    {
        id: 'noticeDate',
        Header: 'historicalNotices.table.dateTime',
        accessor: 'dateTime',
        Cell: ({ value }: { value: string }) => <TruncatedText text={value ?? ''} />,
    },
];
